import { ROLE_PERMISSIONS, type Permission, type Role } from '@sornix/shared';

/**
 * API client.
 *
 * Deliberate choices for a Nigerian multi-site operation:
 *  • the base URL defaults to a *relative* `/api`, so the same bundle works when
 *    served from the LAN bridge (http://192.168.x.x:8080) and from Pages.
 *  • tokens live in localStorage but every response is 401-handled centrally, so
 *    a revoked session kicks the operator straight back to the login screen.
 *  • mutating calls can be queued offline (see offline.ts) — a supermarket till
 *    must not stop selling because the fibre link dropped.
 */

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  meta?: Record<string, unknown>;
}

export interface SessionUser {
  id: string;
  email: string;
  full_name: string;
  global_role: string | null;
  property_roles: Array<{ siteId: string; role: string; scope?: Record<string, unknown> }>;
  sites: Array<{ id: string; name: string; code: string; kind: string }>;
  permissions?: string[];
  two_factor_enabled?: boolean;
  last_login_at?: string | null;
}

const TOKEN_KEY = 'sornix.token';
const REFRESH_KEY = 'sornix.refresh';
const USER_KEY = 'sornix.user';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
  }
}

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const getRefresh = (): string | null => localStorage.getItem(REFRESH_KEY);
export const getStoredUser = (): SessionUser | null => {
  const raw = localStorage.getItem(USER_KEY);
  try {
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
};

export function storeSession(user: SessionUser, token: string, refresh: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_KEY, refresh);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Base URL: relative on purpose, but overridable for the native shells. */
export let API_BASE = ((): string => {
  const injected = (globalThis as { __SORNIX_API__?: string }).__SORNIX_API__;
  if (injected) return injected.replace(/\/$/, '');
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_API_BASE ?? '').replace(/\/$/, '');
})();

/** Updated by the login connection selector after secure native preferences load. */
export function setApiBase(base: string): void {
  API_BASE = base.replace(/\/$/, '');
  (globalThis as { __SORNIX_API__?: string }).__SORNIX_API__ = API_BASE;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the automatic refresh-and-retry (used by the refresh call itself). */
  noRetry?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
  timeoutMs?: number;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http') ? path : `${API_BASE}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timer = opts.timeoutMs ? setTimeout(() => controller.abort(), opts.timeoutMs) : null;

  const headers: Record<string, string> = {
    ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
    ...((opts.headers as Record<string, string>) ?? {}),
  };
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;

  // Tell the API which property is in view. Site-scoped routes carry the id in the
  // path (which is what authorization checks); this header is for audit logs and for
  // any endpoint that wants the client's context without a path parameter.
  const site = localStorage.getItem('sornix.site');
  if (site && !headers['X-Site-Id']) headers['X-Site-Id'] = site;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      ...opts,
      headers,
      signal: opts.signal ?? controller.signal,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch (err) {
    clearTimeout(timer ?? undefined);
    throw new ApiError(
      navigator.onLine ? `Network error reaching the ERP API (${(err as Error).message})` : 'Offline — queued locally',
      0,
      'NETWORK',
    );
  }
  clearTimeout(timer ?? undefined);

  if (res.status === 401 && !opts.noRetry && getRefresh()) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiFetch<T>(path, { ...opts, noRetry: true });
    clearSession();
    window.dispatchEvent(new CustomEvent('sornix:signed-out'));
  }

  const text = await res.text();
  const payload = (text ? JSON.parse(text) : {}) as ApiEnvelope<T>;

  if (!res.ok || payload.success === false) {
    throw new ApiError(payload.error ?? `HTTP ${res.status}`, res.status, (payload as { code?: string }).code);
  }
  return (payload.data ?? (payload as unknown as T)) as T;
}

async function tryRefresh(): Promise<boolean> {
  const refresh = getRefresh();
  if (!refresh) return false;
  try {
    const res = await fetch(buildUrl('/api/v1/auth/refresh'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh, device_label: navigator.userAgent.slice(0, 40) }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as ApiEnvelope<{ access_token: string; refresh_token: string }>;
    if (!body.success || !body.data) return false;
    localStorage.setItem(TOKEN_KEY, body.data.access_token);
    localStorage.setItem(REFRESH_KEY, body.data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiFetch<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/* ------------------------------------------------------------------- auth ---- */

export async function login(email: string, password: string, otp?: string) {
  const data = await apiFetch<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
    user: SessionUser;
  }>('/api/v1/auth/login', {
    method: 'POST',
    body: { email, password, otp, device_label: navigator.userAgent.slice(0, 60) },
    noRetry: true,
  });
  storeSession(data.user, data.access_token, data.refresh_token);
  // Tell the shell a session now exists; see the listener in lib/hooks.ts.
  window.dispatchEvent(new CustomEvent('sornix:signed-in'));
  return data.user;
}

export async function logout(): Promise<void> {
  try {
    await api.post('/api/v1/auth/logout');
  } catch {
    /* the local session is cleared regardless */
  }
  clearSession();
}

export async function fetchMe(): Promise<SessionUser> {
  return apiFetch<SessionUser>('/api/v1/auth/me');
}

/* ---------------------------------------------------------------- helpers ---- */

export const money = (n: number | null | undefined, compact = false): string => {
  const value = Number(n ?? 0);
  if (compact) {
    const abs = Math.abs(value);
    if (abs >= 1e9) return `₦${(value / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `₦${(value / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `₦${(value / 1e3).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(value);
};

export const pct = (n: number | null | undefined): string => `${(Number(n ?? 0)).toFixed(1)}%`;

export const timeAgo = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`).getTime();
  if (Number.isNaN(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

/**
 * Check an explicit API permission first. Only legacy cached sessions that predate
 * the `permissions` field fall back to the shared RBAC matrix; an explicit empty
 * array remains an authoritative deny. Never treat a global role as all-powerful.
 */
export function hasPermission(user: SessionUser | null, permission: string): boolean {
  if (!user) return false;
  if ('permissions' in user) {
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  }

  const roles = [user.global_role, ...(user.property_roles ?? []).map((r) => r.role)];
  return roles.some((value) => {
    if (!value || !Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, value)) return false;
    return ROLE_PERMISSIONS[value as Role].includes(permission as Permission);
  });
}

export function canSeeSite(user: SessionUser | null, siteId: string): boolean {
  if (!user) return false;
  if (user.global_role === 'GROUP_CEO' || user.global_role === 'GROUP_CFO' || user.global_role === 'IT_ADMIN') return true;
  return (user.property_roles ?? []).some((r) => r.siteId === siteId) || (user.sites ?? []).some((s) => s.id === siteId);
}
